import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRecoilState } from 'recoil';
import { DivConst } from 'constants/divConst';
import { AppHelper } from 'helpers/appHelper';
import IndicatorButton from 'components/indicator/IndicatorButton';
import { useErrorBoundary } from 'react-error-boundary';
import { RespondentFieldType } from 'types/respondentFieldType';
import { useNavigate, useParams } from 'react-router-dom';
import { DiagnosisRespondentState } from 'stores/atoms/diagnosisRespondentState';
import { DiagnosisContentState } from 'stores/atoms/diagnosisContentState';
import { RespondentFormStateType } from 'stores/atoms/respondentFormState';
import RespondentForm from 'components/respondent/RespondentForm';
import { useApiState } from 'hooks/useApiState';

/**
 * 回答者情報入力画面
 */
export default function DiagnosisRespondentForm(): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { diagnosisCd, fieldNumber } = useParams<{ diagnosisCd: string; fieldNumber: string }>();
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();
  const [diagnosisContentState] = useRecoilState(DiagnosisContentState);
  const [diagnosisRespondentState, setDiagnosisRespondentState] =
    useRecoilState(DiagnosisRespondentState);
  const [index, setIndex] = useState(0);

  const { postDiagnosisParticipate, postDiagnosisAnswer } = useApiState();

  // 取得項目
  const availableRespondentFields = (diagnosisContentState?.diagnosis?.respondentFields || [])
    .filter(field => field.getFlag)
    .sort((a, b) => (a.no || 0) - (b.no || 0));
  const [respondentFieldState, setRespondentFieldState] = useState<RespondentFieldType>(null);

  const [processingNext, setProcessingNext] = useState(false);
  const [processingPrevious, setProcessingPrevious] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RespondentFormStateType>();

  const reportMailDescribeHtml =
    diagnosisContentState?.diagnosis?.reportMailDescribeHtml ||
    `診断結果と詳細なフィードバックを
    診断完了後にメールでもお送りします。`;

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    const respondentMailDiv = diagnosisContentState.diagnosis?.respondentMailDiv;
    if (
      respondentMailDiv &&
      respondentMailDiv === DivConst.items.diagnosisRespondentMail.beginning.value
    ) {
      setIndex(Number(fieldNumber));
    } else {
      setIndex(diagnosisContentState.diagnosis?.questions?.length + Number(fieldNumber));
    }
    const respondentField = availableRespondentFields[Number(fieldNumber) - 1];
    if (respondentField) {
      setRespondentFieldState(respondentField);
    } else {
      navigate(`/diagnosis/${diagnosisCd}`);
      return;
    }
  }, [fieldNumber]);

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 次へボタン押下時の処理
   */
  const onNext = () => {
    AppHelper.submit(
      processingNext,
      setProcessingNext,
      handleSubmit(async data => {
        try {
          const currentFieldNumber = Number(fieldNumber);
          // 次の項目がある場合
          if (currentFieldNumber < availableRespondentFields.length) {
            // 次の項目の表示
            navigate(`/diagnosis/${diagnosisCd}/form/${currentFieldNumber + 1}`);
          }
          // すべての項目が回答済みの場合
          else {
            // 項目取得タイミングが「診断開始前」の場合は診断参加APIを呼び出し、設問画面に遷移
            if (
              diagnosisContentState.diagnosis.respondentMailDiv ===
              DivConst.items.diagnosisRespondentMail.beginning.value
            ) {
              if (!diagnosisRespondentState.preview) {
                // 診断参加APIを呼び出し、設問画面に遷移
                await postDiagnosisParticipate({
                  diagnosisId: diagnosisContentState.diagnosis.id,
                });
              }
              setDiagnosisRespondentState({
                ...diagnosisRespondentState,
                respondentForm: data,
                page: 'question',
              });
              navigate(`/diagnosis/${diagnosisCd}/question/1`);
            } else {
              // 上記以外は、診断回答APIを呼び出し、結果画面に遷移
              const response = await postDiagnosisAnswer({
                diagnosisId: diagnosisContentState.diagnosis.id,
                diagnosisTitle: diagnosisContentState.diagnosis.title,
                answers: diagnosisRespondentState.answers,
                respondentInfo: {
                  ...diagnosisRespondentState.respondentForm,
                  mailAddress: data.mailAddress,
                  name: data.name,
                  gender: data.gender,
                  birthDate: data.birthDate,
                  phoneNumber: data.phoneNumber,
                  residence: data.residence,
                  customField01: data.customField01,
                  customField02: data.customField02,
                  customField03: data.customField03,
                },
                isPreview: diagnosisRespondentState.preview,
              });
              setDiagnosisRespondentState({
                ...diagnosisRespondentState,
                respondentForm: data,
                page: 'result',
              });
              navigate(`/diagnosis/${diagnosisCd}/result/?cd=${response.diagnosisHistoryCd}`);
            }
          }
        } catch (e) {
          showBoundary(e);
        }
      })
    );
  };

  /**
   * 前へボタン押下時の処理
   */
  const onPrevious = () => {
    AppHelper.submit(processingPrevious, setProcessingPrevious, async () => {
      try {
        // 回答済みの項目数
        const currentFieldNumber = Number(fieldNumber);
        // 前の項目がある場合
        if (currentFieldNumber > 1) {
          // 前の項目の表示
          navigate(`/diagnosis/${diagnosisCd}/form/${currentFieldNumber - 1}`);
        }
      } catch (e) {
        showBoundary(e);
      }
    });
  };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      {respondentFieldState && (
        <main>
          <section className="section-common-quiz">
            <div className="container">
              <div className="card card-quiz">
                <div className="card-body card-body-lg">
                  {/* プログレスバー */}
                  <div className="progress progress-quiz">
                    <div
                      style={{
                        width: `${AppHelper.formatRateToPercent(
                          index,
                          availableRespondentFields.length +
                            diagnosisContentState.diagnosis.questions.length
                        )}%`,
                      }}
                      className="progress-bar"
                      role="progressbar"
                      aria-valuenow={0}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    ></div>
                  </div>
                  {/* 項目表示 */}
                  {/* {respondentFieldState && _renderForm()} */}
                  <RespondentForm
                    respondentField={respondentFieldState}
                    reportMailDescribeHtml={reportMailDescribeHtml}
                    lineFlag={diagnosisContentState.diagnosis.lineFlag}
                    index={index}
                    register={register}
                    errors={errors}
                  />
                  {/* 次へボタン */}
                  <div className="button-container">
                    <span className="ql-button">
                      <a onClick={onNext}>
                        <IndicatorButton visible={processingNext} />
                        つぎへ
                      </a>
                    </span>
                  </div>
                  {/* 戻るボタン (1つ目の時は非表示) */}
                  {Number(fieldNumber) > 1 && (
                    <div className="button-container">
                      <a
                        onClick={onPrevious}
                        className="btn btn-common outline-blue d-block"
                        style={{
                          height: '58px',
                          padding: '16px',
                        }}
                      >
                        <IndicatorButton visible={processingPrevious} />
                        戻る
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        </main>
      )}
    </>
  );
}
